import {
    Box,
    Container,
    Typography,
    Grid,
    Fab,
    Tabs,
    Tab,
} from '@mui/material';
import {
    Add as AddIcon,
    Business as BusinessIcon,
    Home as HomeIcon,
    CheckCircle as CheckCircleIcon,
    Apartment as ApartmentIcon,
} from '@mui/icons-material';
import { useEffect, useState } from 'react';
import { PropertyComplexCard } from '../components/PropertyComplexCard';
import { StatCard } from '../components/StatCard';
import CustomAppBar from '../../../components/shared/CustomAppBar';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { usePropertyInventory } from '../../../hooks/usePropertyInventory';

const HomePage = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState(0);
    const { getApartmentComplexesByOwnerId, apartmentComplexes} = usePropertyInventory();

    const totalComplexUnits = apartmentComplexes.length;
    const totalAvailableComplexUnits = apartmentComplexes.length;
    // const totalAvailableSingleProperties = 0;
    const { userToken } = useAuth();
    const totalProperties = apartmentComplexes.length + 0;
    const totalUnits = totalComplexUnits + 0;
    const totalAvailableUnits = totalAvailableComplexUnits + 0;

    const owner = userToken ? userToken.user.profiles.find(profile => profile.roleId === 4) : null;

    useEffect(() => {
       (async () => {
           if(getApartmentComplexesByOwnerId) {
               await getApartmentComplexesByOwnerId(owner?.id || 0);
           }
       })();
    }, [owner?.id, getApartmentComplexesByOwnerId]);


    const handleTabChange = (event: any, newValue: number) => {
        setActiveTab(newValue);
    };

    return (
        <>
            <CustomAppBar />
            <Box sx={{ minHeight: '100vh', backgroundColor: 'white', mt: '10vh' }}>
                <Container maxWidth="xl" sx={{ py: 4 }}>
                    <Box sx={{
                        textAlign: 'center',
                        mb: 6,
                        py: 8,
                        px: 4,
                        backgroundImage: 'url("/assets/images/property_inventory_home.jpg")',
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                        borderRadius: 3,
                        position: 'relative',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(28, 26, 26, 0.85)',
                            borderRadius: 3,
                            zIndex: 1
                        }
                    }}>
                        <Box sx={{ position: 'relative', zIndex: 2 }}>
                            <Typography
                                gutterBottom
                                sx={{
                                    fontWeight: 'bold',
                                    background: 'linear-gradient(135deg, #0cac87ff 0%, #15bc82ff 100%)',
                                    backgroundClip: 'text',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    mb: 2,
                                    fontSize: { xs: 30, sm: 32, md: 40 }
                                }}
                            >
                                Inventario de Propiedades
                            </Typography>
                            <Typography
                                sx={{ maxWidth: 600, mx: 'auto', lineHeight: 1.6, color: '#fff', fontSize: { xs: 16, sm: 18, md: 20 }, fontWeight: 500 }}
                            >
                                Administra y visualiza tu portafolio de propiedades de manera intuitiva.
                                Controla el estado de cada unidad en tiempo real.
                            </Typography>
                        </Box>
                    </Box>

                    {/* Estadísticas generales */}
                    <Grid container spacing={3} sx={{ mb: 6 }}>
                        <Grid item xs={12} sm={4}>
                            <StatCard
                                title="Unidades Disponibles"
                                value={totalAvailableUnits}
                                color="#4CAF50"
                                icon={<CheckCircleIcon sx={{ fontSize: 30, color: '#4CAF50' }} />}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <StatCard
                                title="Total de Unidades"
                                value={totalUnits}
                                color="#2196F3"
                                icon={<HomeIcon sx={{ fontSize: 30, color: '#2196F3' }} />}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <StatCard
                                title="Total Propiedades"
                                value={totalProperties}
                                color="#FF9800"
                                icon={<BusinessIcon sx={{ fontSize: 30, color: '#FF9800' }} />}
                            />
                        </Grid>
                    </Grid>

                    {/* Tabs para alternar entre secciones */}
                    <Box sx={{ mb: 4 }}>
                        <Tabs 
                            value={activeTab} 
                            onChange={handleTabChange}
                            centered
                            sx={{
                                '& .MuiTabs-indicator': {
                                    backgroundColor: '#4DB6AC',
                                },
                                '& .MuiTab-root': {
                                    color: 'text.secondary',
                                    fontWeight: 'medium',
                                    fontSize: '1rem',
                                    '&.Mui-selected': {
                                        color: '#2E7D7A',
                                        fontWeight: 'bold',
                                    }
                                }
                            }}
                        >
                            <Tab 
                                icon={<ApartmentIcon />} 
                                label={`Complejos (${apartmentComplexes.length})`} 
                                iconPosition="start"
                            />
                            <Tab 
                                icon={<HomeIcon />} 
                                label={`Propiedades Solitarias (${0})`} 
                                iconPosition="start"
                            />
                        </Tabs>
                    </Box>

                    {/* Contenido según tab activo */}
                    {activeTab === 0 ? (
                        // Sección de Complejos
                        <Box>
                            <Typography variant="h5" fontWeight="bold" color="#2E7D7A" sx={{ mb: 3 }}>
                                Complejos de Apartamentos
                            </Typography>
                            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                                Gestiona edificios y residenciales con múltiples unidades
                            </Typography>
                            <Grid container spacing={4}>
                                {apartmentComplexes.length > 0 && apartmentComplexes?.map((complex) => (
                                    <Grid item xs={12} sm={6} lg={4} key={complex.id}>
                                        <PropertyComplexCard
                                            complex={complex}
                                            onClick={() => navigate(`/properties-inventory/complex/${complex.id}`)}
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                    ) : (
                        // Sección de Propiedades Solitarias
                        <Box>
                            <Typography variant="h5" fontWeight="bold" color="#2E7D7A" sx={{ mb: 3 }}>
                                Propiedades Individuales
                            </Typography>
                            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                                Administra casas y apartamentos individuales
                            </Typography>
                            <Grid container spacing={4}>
                                {/* {singleProperties.map((property) => (
                                    <Grid item xs={12} sm={6} lg={4} key={property.id}>
                                        <SinglePropertyCard
                                            property={property}
                                            onClick={() => navigate(`/properties-inventory/single-property/${property.id}`)}
                                        />
                                    </Grid>
                                ))} */}
                                <Grid item xs={12}>
                                    <Box display="flex" justifyContent="center">
                                        <Box
                                            sx={{
                                                width: '100%',
                                                maxWidth: 640,
                                                textAlign: 'center',
                                                p: 4,
                                                borderRadius: 3,
                                                background: 'linear-gradient(180deg, rgba(77,182,172,0.06) 0%, rgba(38,166,154,0.02) 100%)',
                                                border: '1px solid rgba(77,182,172,0.12)',
                                                boxShadow: '0 6px 18px rgba(37,150,140,0.06)',
                                                mx: 2,
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                                                <Box
                                                    sx={{
                                                        width: 88,
                                                        height: 88,
                                                        borderRadius: '50%',
                                                        background: 'linear-gradient(135deg, #26A69A 0%, #4DB6AC 100%)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        boxShadow: '0 10px 30px rgba(38,166,154,0.18)',
                                                    }}
                                                >
                                                    <BusinessIcon sx={{ fontSize: 40, color: '#fff' }} />
                                                </Box>
                                            </Box>

                                            <Typography variant="h6" fontWeight="bold" color="#2E7D7A" sx={{ mb: 1 }}>
                                                En desarrollo...
                                            </Typography>
                                            <Typography color="text.secondary" sx={{ maxWidth: 520, mx: 'auto' }}>
                                                Esta sección de Propiedades Individuales está en construcción. Pronto podrás ver y administrar tus casas
                                                y apartamentos individuales aquí. Mientras tanto, puedes crear nuevas propiedades desde el botón (+).
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Box>
                    )}
                </Container>

                <Fab
                    color="primary"
                    aria-label="add"
                    onClick={() => navigate("/properties-inventory/create-property")}
                    sx={{
                        position: 'fixed',
                        bottom: 32,
                        right: 32,
                        background: 'linear-gradient(135deg, #26A69A 0%, #4DB6AC 100%)',
                        '&:hover': {
                            background: 'linear-gradient(135deg, #00897B 0%, #26A69A 100%)',
                            transform: 'scale(1.1)',
                        },
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                >
                    <AddIcon />
                </Fab>
            </Box>
        </>
    );
};

export default HomePage;